import React, { useState, useEffect, useRef } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import Header from '~/pages/Manager/Header';
import {
  getEquipments,
  getEquipmentsTemplate,
  exportEquipments,
  importEquipments,
  resetFileUploadErrors,
  getEquipmentDetails,
  getEquipmentTypes,
  getEquipmentsGroups,
  addEquipment,
  resetEquipments,
  setEquipmentsValues,
  updateEquipments,
} from '~/store/manager/actions';
import styles from '../styles';
import PF2MDataTable from '~/components/PF2MDataTable';
import { getLoadAllocations, getTruckAllocations } from '~/store/dispatch/actions';
import {
  validateIdEquip,
  validateIdGroup,
  validateName,
  validateDescription,
  validatePassword,
  validateWeighingTare,
  validateLicensePlate,
  validateIsReserve,
  validateCompany,
  validateStandardLoad,
  validateActive,
  isEquipWithStandardLoad,
} from './validate';

const EquipmentsTab = ({
  classes,
  equipments,
  getEquipments: getAll,
  getEquipmentsTemplate: getTemplateAction,
  importEquipments: importAction,
  resetEquipments: reset,
  setEquipmentsValues: setValues,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState('');
  const fileUploadSuccess = useSelector(state => state.errors.fileUploadSuccess);
  const equipmentDetails = useSelector(state => state.manager.equipmentDetails);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const loadAllocations = useSelector(state => state.dispatch.loadAllocations).filter(r => r.id);
  const truckAllocations = useSelector(state => state.dispatch.truckAllocations).filter(r => r.id);

  const tableRef = useRef();

  useEffect(() => {
    async function fetchData() {
      await getAll(true);
      dispatch(getLoadAllocations());
      dispatch(getTruckAllocations());
      dispatch(getEquipmentDetails());
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentsGroups(false));
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getAll]);

  const warningPopup = () => {
    const message = (
      <>
        {translate('validation:CompleteTheRegistration')}
        <ul style={styles.listStyle}>
          <li>{translate('common:Checklist')}</li>
          <li>{translate('common:Hourmeter')}</li>
          <li>
            {translate('common:AverageLoad')}
            {' '}
            (
            {translate('common:TypesCargo')}
            )
          </li>
          <li>
            {translate('common:DrillingInformation')}
            {' '}
            (
            {translate('common:DrillingEquipment')}
            )
          </li>
        </ul>
      </>
    );
    setSuccessDialogMessage(message);
    setShowSuccessDialog(true);
  };

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
      warningPopup();
    } else if (fileUploadSuccess === `${translate('validation:SheetImportSuccess')}`) {
      warningPopup();
    }
  }, [fileUploadErrors, fileUploadSuccess]);

  const normalizedStandardLoadEquips = equipments.map(equip => ({
    ...equip,
    standard_load: equip.update_status ? equip.standard_load : (equipmentDetails.find(d => String(d.equip_id) === String(equip.id))?.avg_load_default ?? ''),
  }));

  const handleApplyEquipments = () => {
    const hasPendingChanges = normalizedStandardLoadEquips.some(row => row.update_status);
    if (!hasPendingChanges) return;
    dispatch(updateEquipments(normalizedStandardLoadEquips))
      .then(() => {
        dispatch(getEquipmentDetails());
      });
  };

  const columns = [
    {
      field: 'id_equip',
      title: translate('common:Type'),
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      lookup: equipmentTypes,
      required: true,
      validate: validateIdEquip,
      customOnChange: () => ({ id_group: '0', standard_load: '' }),
    },
    {
      field: 'id_group',
      title: translate('common:Group'),
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      required: true,
      lookup: equipmentsGroups,
      customType: 'filterLookup',
      validate: validateIdGroup,
      lookupFilter: (lookup, rowData) => String(lookup.id_equipament) === String(rowData.id_equip) || String(rowData.id_equip) === '0',
    },
    {
      field: 'name',
      title: translate('common:Name'),
      editable: 'always',
      // unique: true,
      required: true,
      validate: rowData => validateName(rowData, tableRef),
    },
    {
      field: 'description',
      title: translate('common:Description'),
      editable: 'always',
      required: true,
      validate: validateDescription,
    },
    {
      field: 'password',
      title: translate('common:Password'),
      editable: 'always',
      required: true,
      validate: validatePassword,
    },
    {
      field: 'weighing_tare',
      title: translate('common:WeighingTare'),
      editable: 'always',
      validate: validateWeighingTare,
    },
    {
      field: 'license_plate',
      title: translate('common:Plate'),
      editable: 'always',
      unique: true,
      validate: rowData => validateLicensePlate(rowData, tableRef),
    },
    {
      field: 'is_reserve',
      title: translate('common:ReserveEquipment'),
      lookupKey: 'value',
      lookup: [
        { name: translate('common:No'), value: 0 },
        { name: translate('common:Yes'), value: 1 },
      ],
      required: true,
      validate: validateIsReserve,
    },
    {
      field: 'company',
      title: translate('common:Company'),
      editable: 'always',
      validate: validateCompany,
    },
    {
      field: 'standard_load',
      title: translate('common:StandardLoad'),
      editable: (_, rowData) => isEquipWithStandardLoad(String(rowData.id_equip)),
      required: (_, rowData) => isEquipWithStandardLoad(String(rowData.id_equip)),
      validate: validateStandardLoad,
    },
    {
      field: 'active',
      title: translate('common:STATUS'),
      editable: 'always',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Inactive'), value: 0 },
        { name: translate('common:Active'), value: 1 },
      ],
      defaultFilter: isEditing ? undefined : ['1'],
      required: true,
      validate: rowData => validateActive(rowData, { loadAllocations, truckAllocations }),
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <Header
        getTemplateAction={getTemplateAction}
        importAction={async (file) => {
          await importAction(file);
          dispatch(getEquipmentDetails());
        }}
      />
      <PF2MDataTable
        ref={tableRef}
        options={{
          search: true,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
        }}
        columns={columns}
        data={normalizedStandardLoadEquips}
        onAdd={newData => dispatch(addEquipment(newData))}
        onChange={(newData) => {
          setIsEditing(true);
          newData.forEach((row) => {
            setValues({
              ...row,
              name: row.name.trim(),
              description: row.description.trim(),
              license_plate: (row.license_plate || '').trim(),
            });
          });
        }}
        onDiscard={() => reset()}
        onApply={handleApplyEquipments}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
      <PF2MAlertDialog
        title={translate('common:RegistrationSteps')}
        description={
          <div style={styles.descriptionStyle}>{successDialogMessage}</div>
        }
        open={showSuccessDialog}
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
        onClose={() => setShowSuccessDialog(false)}
        hasCancel={false}
        badge
      />
    </div>
  );
};

EquipmentsTab.propTypes = {
  classes: PropTypes.object.isRequired,
  equipments: PropTypes.array,
  getEquipments: PropTypes.func,
  getEquipmentsTemplate: PropTypes.func,
  importEquipments: PropTypes.func,
  resetEquipments: PropTypes.func,
  setEquipmentsValues: PropTypes.func,
};

EquipmentsTab.defaultProps = {
  equipments: [],
  getEquipments: () => { },
  getEquipmentsTemplate: () => { },
  importEquipments: () => { },
  resetEquipments: () => { },
  setEquipmentsValues: () => { },
};

const mapStateToProps = state => ({
  equipments: state.manager.equipments,
});

const mapDispatchToProps = {
  getEquipments,
  getEquipmentsTemplate,
  exportEquipments,
  importEquipments,
  updateEquipments,
  resetEquipments,
  setEquipmentsValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EquipmentsTab));
